/**
 * Profile Hit Rate Widget (2026-05-05)
 *
 * Compact KPI showing the live accuracy of the SET PROFILE FIRST
 * archetype classifier on settled matches. Polls
 * `/api/tt/admin/set-profile-hit-rate` and renders:
 *
 *   - aggregate strict + compatible accuracy (single big number)
 *   - settled sample size
 *   - per-archetype mini-bars (STOMP/FAST/LONG/CHAOS/BALANCED)
 *
 * Audit-only — no actions, no Telegram side-effects.
 */
import React, { useEffect, useState } from 'react';
import { adminGet } from '../data/api';
import { Target, RefreshCcw } from 'lucide-react';

const ARCHETYPES = ['STOMP', 'FAST', 'LONG', 'CHAOS', 'BALANCED'];
const ARCH_COLOR = {
  STOMP: 'text-violet-300 bg-violet-900/30',
  FAST: 'text-emerald-300 bg-emerald-900/30',
  LONG: 'text-amber-300 bg-amber-900/30',
  CHAOS: 'text-rose-300 bg-rose-900/30',
  BALANCED: 'text-zinc-300 bg-zinc-800/40',
};

const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

export default function ProfileHitRateWidget({ pin, days = 14, autoLoad = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await adminGet(
        `/tt/admin/set-profile-hit-rate?days=${days}`,
        pin,
      );
      setData(r);
    } catch (e) {
      setErr(e?.message || 'load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoad) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, days]);

  const strict = data?.hit_rate_strict_pct;
  const compat = data?.hit_rate_compatible_pct;

  return (
    <div
      data-testid="profile-hit-rate-widget"
      className="rounded-sm border border-zinc-800 bg-zinc-950/40 p-3 text-[11px] space-y-2"
      style={{ fontFamily: 'monospace' }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target size={13} className="text-emerald-400" />
          <span className="text-zinc-100 font-semibold tracking-wide">
            PROFILE HIT RATE
          </span>
          <span className="text-[9px] text-zinc-500">
            last {days}d
          </span>
        </div>
        <button
          data-testid="profile-hit-rate-reload"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1 px-2 py-1 rounded-sm bg-zinc-800/60 border border-zinc-700 text-zinc-300 hover:text-zinc-100 disabled:opacity-50"
        >
          <RefreshCcw size={11} className={loading ? 'animate-spin' : ''} />
          {loading ? 'loading' : 'reload'}
        </button>
      </div>

      {err && (
        <div className="text-rose-300 px-2 py-1 rounded-sm bg-rose-950/40 border border-rose-800">
          {err}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-3 gap-2 pt-1">
            <div className="rounded-sm bg-zinc-900/60 border border-zinc-800 p-2">
              <div className="text-[9px] text-zinc-500 tracking-wider">
                STRICT
              </div>
              <div
                data-testid="hit-rate-strict"
                className="text-emerald-300 text-lg font-bold"
              >
                {fmtPct(strict)}
              </div>
              <div className="text-[9px] text-zinc-500">
                exact archetype match
              </div>
            </div>
            <div className="rounded-sm bg-zinc-900/60 border border-zinc-800 p-2">
              <div className="text-[9px] text-zinc-500 tracking-wider">
                COMPATIBLE
              </div>
              <div
                data-testid="hit-rate-compatible"
                className="text-violet-300 text-lg font-bold"
              >
                {fmtPct(compat)}
              </div>
              <div className="text-[9px] text-zinc-500">
                cluster ⊇ archetype
              </div>
            </div>
            <div className="rounded-sm bg-zinc-900/60 border border-zinc-800 p-2">
              <div className="text-[9px] text-zinc-500 tracking-wider">
                SAMPLE
              </div>
              <div
                data-testid="hit-rate-sample"
                className="text-zinc-100 text-lg font-bold"
              >
                {data.n_settled ?? 0}
              </div>
              <div className="text-[9px] text-zinc-500">
                settled / {data.n_analyses ?? 0} analyzed
              </div>
            </div>
          </div>

          <div className="pt-1 border-t border-zinc-800">
            <div className="text-[9px] text-zinc-500 mb-1 tracking-wider">
              PER ARCHETYPE
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-1">
              {ARCHETYPES.map((arch) => {
                const v = data.per_archetype?.[arch];
                const acc = v?.accuracy_compatible_pct;
                const color = ARCH_COLOR[arch];
                return (
                  <div
                    key={arch}
                    data-testid={`hit-rate-arch-${arch}`}
                    className={`rounded-sm p-1.5 ${color}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-semibold tracking-wider">
                        {arch}
                      </span>
                      <span className="text-[10px] font-bold">
                        {fmtPct(acc)}
                      </span>
                    </div>
                    <div className="text-[9px] opacity-70">
                      n={v?.settled ?? 0} settled
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
